import { LuInfo, LuTag, LuX } from 'react-icons/lu'

import { AlertTagsPopout, ModalOpenVariant, TagsList } from '@/feature/tags'
import { ModalBase } from '@/lib/core/components/Modal/ModalBase'
import { RadioButtons } from '@/lib/core/components/RadioButtons'
import { Tooltip } from '@/lib/core/components/Tooltip'

import { ALERT_SEVERITY_BY_TEMPLATE, AlertTemplateKey } from '../../constants/alertContract'
import { AlertSeverityBadge } from '../AlertSeverityBadge'
import styles from './CoreAlertsModal.module.css'
import { ALERT_SCOPE_OPTIONS, CORE_ALERT_TOOLTIPS } from './coreAlertUtils'
import { useCoreAlertForm } from './useCoreAlertForm'

export const CoreAlertsModal = () => {
  const {
    isCoreAlertOpen,
    closeCoreAlert,
    selectedTemplate,
    isEditMode,
    isMutating,
    name,
    contacts,
    errors,
    fieldErrorKeys,
    resolvedFieldValues,
    taggableAlert,
    showTagSelector,
    alertScope,
    handleAlertScopeChange,
    tagsUnsupportedMessage,
    alertTags,
    tagIconRef,
    openModal,
    handleTagIconClick,
    handleNameChange,
    handleContactsChange,
    handleFieldChange,
    handleSave,
  } = useCoreAlertForm()

  const severity = selectedTemplate
    ? ALERT_SEVERITY_BY_TEMPLATE[selectedTemplate.key as AlertTemplateKey]
    : undefined

  if (!selectedTemplate) return null

  return (
    <ModalBase isActive={isCoreAlertOpen} onEscape={closeCoreAlert}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>{isEditMode ? 'Edit Alert' : 'Create Alert'}</span>
          <button className={styles.closeButton} type="button" onClick={closeCoreAlert}>
            <LuX size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.section}>
            <label className={styles.label}>Name Alert</label>
            <input
              className={[styles.input, errors.name ? styles.inputError : ''].join(' ')}
              type="text"
              value={name}
              placeholder="Untitled"
              onChange={(e) => handleNameChange(e.target.value)}
            />
            {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
          </div>

          <div
            className={`${styles.templateCard} ${severity ? styles[`templateBorder_${severity}`] : ''}`}
          >
            <div className={styles.templateHeader}>
              <div className={styles.templateTitleRow}>
                <span className={styles.templateTitle}>{selectedTemplate.title}</span>
                {selectedTemplate.tooltipText && (
                  <Tooltip
                    tooltipContent={selectedTemplate.tooltipText}
                    placement="top"
                    maxWidth={320}
                  >
                    <span className={styles.infoIcon} data-testid="alert-title-tooltip">
                      <LuInfo size={16} />
                    </span>
                  </Tooltip>
                )}
              </div>
              {severity && <AlertSeverityBadge severity={severity} />}
            </div>
            {selectedTemplate.description && (
              <p className={styles.templateDescription}>{selectedTemplate.description}</p>
            )}
            <div className={styles.templateBody}>
              {selectedTemplate.renderBody?.(
                resolvedFieldValues,
                handleFieldChange,
                fieldErrorKeys,
              )}
              {errors.fields?.map((msg) => (
                <div key={msg} className={styles.errorMessage}>
                  {msg}
                </div>
              ))}
            </div>

            {taggableAlert && (
              <div className={styles.scopeRow} data-testid="alert-scope-row">
                <label className={styles.scopeLabel}>Send alert for</label>
                <RadioButtons
                  key={alertScope}
                  name="alert-scope"
                  additionalStyles={{ minHeight: 40 }}
                  currentValue={alertScope}
                  options={[...ALERT_SCOPE_OPTIONS]}
                  onChange={handleAlertScopeChange}
                />
              </div>
            )}

            {showTagSelector && (
              <div className={styles.tagsRow} data-testid="alert-tags-row">
                <label className={styles.tagsLabel}>Tags</label>
                <div className={styles.tagsSection}>
                  <div
                    ref={tagIconRef}
                    className={styles.tagsIcon}
                    onClick={handleTagIconClick}
                    data-testid="alert-tags-icon"
                  >
                    <LuTag size={16} />
                  </div>
                  <TagsList
                    maxTagsRenderCount={5}
                    openModal={openModal}
                    modalVariant={ModalOpenVariant.ALERT}
                    tagIconRef={tagIconRef}
                    tagsList={alertTags}
                  />
                </div>
              </div>
            )}

            {tagsUnsupportedMessage && (
              <p className={styles.tagsUnsupported} data-testid="alert-tags-unsupported">
                {tagsUnsupportedMessage}
              </p>
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.contactsLabel}>
              <span>Set Contacts</span>
              <Tooltip
                tooltipContent={CORE_ALERT_TOOLTIPS.setContacts}
                placement="top"
                maxWidth={300}
              >
                <span>
                  <LuInfo size={16} className={styles.infoIcon} />
                </span>
              </Tooltip>
            </div>
            <input
              className={[styles.input, errors.contacts ? styles.inputError : ''].join(' ')}
              type="text"
              value={contacts}
              placeholder="contact number and/or email address"
              onChange={(e) => handleContactsChange(e.target.value)}
            />
            {errors.contacts && <span className={styles.errorMessage}>{errors.contacts}</span>}
          </div>
        </div>

        {errors.submit && (
          <div className={styles.submitError}>
            <span className={styles.errorMessage}>{errors.submit}</span>
          </div>
        )}
        <div className={styles.footer}>
          <button
            className={`${styles.footerButton} ${styles.cancelButton}`}
            type="button"
            onClick={closeCoreAlert}
          >
            Close
          </button>
          <button
            className={`${styles.footerButton} ${styles.saveButton}`}
            type="button"
            onClick={handleSave}
            disabled={isMutating}
          >
            {isMutating ? 'Saving...' : 'Save'}
          </button>
        </div>

        {showTagSelector && <AlertTagsPopout />}
      </div>
    </ModalBase>
  )
}
